import { z } from 'zod'
import { baseEntitySchema } from '../../core/entity.js'

/**
 * Wall-clock ceiling for a single agent turn, in seconds. The single source
 * of truth consumed by BOTH the agent-stream Lambda's CDK timeout and the
 * client's runtime-status staleness cutoff — so the client can never assume
 * a turn is dead while the server is still allowed to run it.
 */
export const AGENT_TURN_MAX_SECONDS = 180

export const agentRoles = ['song-agent', 'project-agent', 'folder-agent', 'generic'] as const
export type AgentRole = (typeof agentRoles)[number]
export const agentRoleSchema = z.enum(agentRoles)

export const agentStatuses = ['active', 'paused', 'archived'] as const
export type AgentStatus = (typeof agentStatuses)[number]
export const agentStatusSchema = z.enum(agentStatuses)

/**
 * Live runtime state. Orthogonal to the persistent `status` field
 * (which is the user's intent — `active` / `paused` / `archived`).
 * `runtimeStatus` is "is the agent currently executing a turn?" and
 * is broadcast via WebSocket partial-entity updates without touching
 * DynamoDB. Default-undefined means idle.
 */
export const agentRuntimeStatuses = ['idle', 'thinking', 'error'] as const
export type AgentRuntimeStatus = (typeof agentRuntimeStatuses)[number]
export const agentRuntimeStatusSchema = z.enum(agentRuntimeStatuses)

export const agentProactivityLevels = ['off', 'low', 'normal', 'high'] as const
export type AgentProactivity = (typeof agentProactivityLevels)[number]
export const agentProactivitySchema = z.enum(agentProactivityLevels)

/**
 * Per-user, per-agent "last read" cursors. Keyed by agentId, value is the
 * ISO-8601 timestamp the user last opened that agent's panel. Stored on
 * the user's profile config (cross-device sync free) and consulted by the
 * Agent Rail to decide whether to render an "unread" dot.
 */
export const agentReadCursorsSchema = z.record(
  z.string().describe('agentId'),
  z.string().describe('ISO-8601 timestamp of last read')
)
export type AgentReadCursors = z.infer<typeof agentReadCursorsSchema>

export const agentTriggerConfigSchema = z.object({
  onChange: z.boolean().optional(),
  idleDays: z.number().int().positive().optional(),
  sessionSignals: z.boolean().optional(),
  goals: z.boolean().optional(),
})

export const agentConfigSchema = z.object({
  proactivity: agentProactivitySchema.default('normal'),
  triggers: agentTriggerConfigSchema.default({}),
  allowedTools: z.array(z.string()).optional(),
})

export const agentGoalSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  dueAt: z.string().describe('ISO-8601 deadline').optional(),
  completed: z.boolean().optional(),
  createdAt: z.string().describe('ISO-8601 creation timestamp'),
})
export type AgentGoal = z.infer<typeof agentGoalSchema>

export const agentMemorySchema = z.object({
  summary: z.string().optional(),
  facts: z.array(z.string()).optional(),
  openQuestions: z.array(z.string()).optional(),
})
export type AgentMemory = z.infer<typeof agentMemorySchema>

export const agentSchema = baseEntitySchema.extend({
  type: z.literal('agent'),
  /** Links to the AgentDefinition.id from the plugin that created this agent */
  definitionId: z.string().optional(),
  /** Custom role line for the system prompt preamble. Overrides role-based default. */
  roleLine: z.string().optional(),
  role: agentRoleSchema,
  status: agentStatusSchema.default('active'),
  /** Model id override. Stored as a plain string so schemas/ stays independent of ai-lib. */
  model: z.string().optional(),
  config: agentConfigSchema,
  goals: z.array(agentGoalSchema).optional(),
  memory: agentMemorySchema.optional(),
  /** ISO-8601 timestamp of the most recent proactive run. Used to rate-limit nudges. */
  lastRunAt: z.string().optional(),
  /** ISO-8601 timestamp of the most recent user-visible nudge. */
  lastNudgeAt: z.string().optional(),
  /**
   * Live runtime state — transient, never persisted to DDB.
   * Pushed via WebSocket `receivePartialEntity` envelopes. Default-undefined
   * means idle. See `agentRuntimeStatuses` for axis docs.
   */
  runtimeStatus: agentRuntimeStatusSchema.describe('Transient runtime state').optional(),
  /** ISO-8601 timestamp the runtimeStatus was last asserted. Used for client-side TTL. */
  runtimeStatusAt: z.string().describe('ISO-8601 of last runtime status assert').optional(),
  /** Human-readable error blurb when runtimeStatus === 'error'. */
  runtimeError: z.string().describe('Error blurb for runtimeStatus error').optional(),
  /**
   * Example prompts the user can click as a starter when the agent's chat is empty.
   * Renders as chips in the empty-state header above the chat feed. Plugins authoring
   * an `AgentDefinition` may seed this from their definition; falls back to role-keyed
   * defaults at render time when omitted.
   */
  examplePrompts: z.array(z.string()).describe('Starter prompts for the empty chat state').optional(),
  /**
   * Material color name used by the rail card, sidebar tab marker, palette
   * icon, and chat avatar. Plain `string` rather than an enum because the
   * registry of valid palette names lives in `@tunasong/ui-lib` (importing
   * it here would cross a circular-dep line). Runtime validation against
   * the registry happens at render time via `getColorCSS()`; an unknown
   * value gracefully falls back to `inherit`.
   */
  color: z
    .string()
    .describe('Color token name (one of the material color palette names — see @tunasong/ui-lib getColorCSS).')
    .optional(),
  /**
   * Icon registry key for the agent. Plain `string` rather than an enum
   * for the same reason as `color` — the registry lives in `@tunasong/icons`
   * and lookup is deferred to render time. Unknown values fall back to the
   * role-keyed default.
   */
  iconKey: z.string().describe('Icon registry key for the agent (see @tunasong/icons).').optional(),
})

export type Agent = z.infer<typeof agentSchema>

export function isAgent(node?: unknown): node is Agent {
  return agentSchema.safeParse(node).success
}
